/**
 * useDiagnosticPlan - Hook for generating diagnostic test recommendations
 *
 * Calls the AI service workflow endpoint to generate recommended diagnostic tests
 * based on selected diagnoses and patient encounter transcript.
 */

import { useState, useCallback, useRef } from "react";
import { invokeLLMPrompt } from "services/llmService";
import { patientContextPayloadKey } from "utils/patientContext";
import { useAuth } from "hooks/useAuth";
import { isMissingPromptError } from "components/dashboard/chartmind/pearls";
import { processExam2Extras as normalizeExam2Extras } from "components/dashboard/chartmind/exam2Catalog";

// Canonical id; older / incomplete environments may only have the SCMAP4 twin.
const DIAGNOSTIC_PLAN_WORKFLOW_NAME = "chartmind-plan";
const DIAGNOSTIC_PLAN_FALLBACK_PROMPT_ID = "chartmind-diagnostic-tests-scmap4";

/**
 * Parse LLM response to extract JSON
 */
function parseResponse(content) {
  if (!content) return null;

  const trimmed = content.trim();

  // Check for non-JSON responses
  if (
    trimmed.startsWith("I'm sorry") ||
    trimmed.startsWith("I cannot") ||
    !trimmed.includes("{")
  ) {
    console.log("[useDiagnosticPlan] LLM returned non-JSON response");
    return {
      examFindings: [],
      exam2Extras: [],
      tests: [],
      testingStrategy: "",
      considerations: [],
    };
  }

  // Extract JSON from markdown code blocks if present
  let jsonStr = content;
  const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  try {
    return JSON.parse(jsonStr.trim());
  } catch (error) {
    console.error("[useDiagnosticPlan] Failed to parse JSON:", error);
    return null;
  }
}

/**
 * Normalize test category
 */
function normalizeCategory(category) {
  if (!category) return "other";
  const lower = category.toLowerCase();
  if (lower.includes("lab")) return "lab";
  if (lower.includes("imag")) return "imaging";
  if (lower.includes("proc")) return "procedure";
  return "other";
}

/**
 * Process tests to ensure consistent structure
 */
function processTests(tests) {
  if (!Array.isArray(tests)) return [];

  return tests.map((test, index) => ({
    id: test.id || `test-${index}-${Date.now()}`,
    name: test.name || "Unknown Test",
    category: normalizeCategory(test.category),
    rationale: test.rationale || "",
    linkedDiagnoses: test.linkedDiagnoses || test.linked_diagnoses || [],
    priority: test.priority || test.urgency || "routine",
    turnaroundTime: test.turnaroundTime || test.turnaround_time || null,
    estimatedCost: test.estimatedCost || test.estimated_cost || null,
  }));
}

/**
 * Process focused physical-exam recommendations (Events Plan "Exam" section).
 * anticipatedFindings are abnormal/variant choices for Objective documentation (A–Z).
 */
function processExamFindings(findings) {
  if (!Array.isArray(findings)) return [];

  return findings
    .map((finding, index) => {
      const name = String(finding?.name || finding?.finding || finding?.maneuver || "").trim();
      if (!name) return null;
      const anticipatedRaw =
        finding.anticipatedFindings ||
        finding.anticipated_findings ||
        finding.findings ||
        [];
      const anticipatedFindings = Array.isArray(anticipatedRaw)
        ? [
            ...new Set(
              anticipatedRaw
                .map((item) => String(item || "").trim())
                .filter(Boolean),
            ),
          ].sort((a, b) => a.localeCompare(b))
        : [];
      return {
        id: finding.id || `exam-${index}-${Date.now()}`,
        name,
        rationale: String(finding.rationale || "").trim(),
        linkedDiagnoses: finding.linkedDiagnoses || finding.linked_diagnoses || [],
        anticipatedFindings,
      };
    })
    .filter(Boolean);
}

/**
 * Process optional Exam2 AI extras (additional systems / option addenda).
 * Full merge with the base catalog happens in the session via mergeExam2Systems.
 */
function processExam2Extras(extras) {
  return normalizeExam2Extras(extras).map(({ value: _value, source: _source, ...rest }) => rest);
}

/**
 * useDiagnosticPlan Hook
 *
 * @param {Object} options - Hook options
 * @param {Function} options.onDataChanged - Callback fired after successful AI response (for auto-save)
 * @param {string} options.promptId - Prompt to generate with. Defaults to ChartMind's own;
 *   ClinicalHUD2 passes its own.
 * @param {string} [options.fallbackPromptId] - Used if `promptId` is not set up on this environment
 * @param {string} [options.focus] - Specialty and setting focus, appended to the request
 */
function useDiagnosticPlan({
  onDataChanged,
  promptId = DIAGNOSTIC_PLAN_WORKFLOW_NAME,
  fallbackPromptId,
  focus,
} = {}) {
  const { organizationId } = useAuth();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);

  // Track to prevent duplicate calls
  const lastRequestRef = useRef("");
  const isGeneratingRef = useRef(false);

  /**
   * Generate diagnostic plan based on diagnoses and transcript
   */
  const generatePlan = useCallback(async (transcript, selectedDiagnoses, patientContext = null) => {
    // Validate inputs
    if (!transcript || transcript.trim().length < 50) {
      console.log("[useDiagnosticPlan] Transcript too short");
      return;
    }

    if (!selectedDiagnoses || selectedDiagnoses.length === 0) {
      console.log("[useDiagnosticPlan] No diagnoses selected");
      return;
    }

    // Create request key to detect duplicates
    const diagnosisNames = selectedDiagnoses
      .map((d) => d.condition || d.name)
      .join(",");
    // Includes the patient context so importing a record mid-encounter re-fires the step
    // instead of serving the record-blind cached plan. Prompt id + focus so specialty/setting
    // changes refetch rather than reuse another stance's cache.
    const requestKey = `${promptId}|${focus || ""}|${transcript.slice(0, 100)}|${diagnosisNames}|${patientContextPayloadKey(patientContext)}`;

    // Prevent duplicate calls
    if (isGeneratingRef.current) {
      console.log("[useDiagnosticPlan] Already generating, skipping");
      return;
    }

    if (requestKey === lastRequestRef.current && planData) {
      console.log("[useDiagnosticPlan] Same request, using cached result");
      return;
    }

    isGeneratingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Format diagnoses for the prompt
      const diagnosisText = selectedDiagnoses
        .map((d) => `- ${d.condition || d.name} (${d.likelihood || "Likely"})`)
        .join("\n");

      console.log("[useDiagnosticPlan] Calling workflow...", {
        workflowName: promptId,
        diagnosesCount: selectedDiagnoses.length,
      });

      const resolvedFallback =
        fallbackPromptId ||
        (promptId === DIAGNOSTIC_PLAN_WORKFLOW_NAME
          ? DIAGNOSTIC_PLAN_FALLBACK_PROMPT_ID
          : undefined);

      const result = await invokeLLMPrompt({
        promptId,
        // caremap2020 (and some sandboxes) never got chartmind-plan; the SCMAP4 id is the
        // same ChartMind diagnostic-tests prompt and returns the same JSON shape.
        ...(resolvedFallback ? { fallbackPromptId: resolvedFallback } : {}),
        ...(focus ? { focus } : {}),
        userMessage: `PATIENT ENCOUNTER TRANSCRIPT:\n\n${transcript}\n\nSELECTED DIAGNOSES:\n${diagnosisText}`,
        organizationId,
        patientContext,
      });

      console.log("[useDiagnosticPlan] Workflow response received");

      const responseText = result.output;
      setRawResponse(responseText);

      const parsed = parseResponse(responseText);

      if (parsed) {
        const processedData = {
          examFindings: processExamFindings(
            parsed.examFindings || parsed.exam_findings || parsed.exam || [],
          ),
          exam2Extras: processExam2Extras(
            parsed.exam2Extras || parsed.exam2_extras || parsed.exam2Systems || [],
          ),
          tests: processTests(parsed.tests),
          testingStrategy: parsed.testingStrategy || parsed.testing_strategy || "",
          considerations: parsed.considerations || [],
        };

        setPlanData(processedData);
        lastRequestRef.current = requestKey;

        console.log("[useDiagnosticPlan] Plan generated:", {
          examCount: processedData.examFindings.length,
          exam2ExtraCount: processedData.exam2Extras.length,
          testCount: processedData.tests.length,
        });
        
        // Trigger auto-save after successful AI response
        if (onDataChanged) {
          console.log("[useDiagnosticPlan] Triggering auto-save callback");
          onDataChanged();
        }
      } else {
        setError("Failed to parse diagnostic plan response");
      }
    } catch (err) {
      console.error("[useDiagnosticPlan] Error:", err);
      // Missing prompt → empty list + Add Test, not a Query Manager error under the heading.
      if (isMissingPromptError(err)) {
        setPlanData({
          examFindings: [],
          exam2Extras: [],
          tests: [],
          testingStrategy: "",
          considerations: [],
        });
        lastRequestRef.current = requestKey;
        setError(null);
      } else {
        setError(err.message || "Failed to generate diagnostic plan");
      }
    } finally {
      isGeneratingRef.current = false;
      setLoading(false);
    }
  }, [planData, onDataChanged, organizationId, promptId, fallbackPromptId, focus]);

  /**
   * Add a custom test to the plan
   */
  const addTest = useCallback((test) => {
    setPlanData((prev) => {
      const newTest = {
        id: `custom-${Date.now()}`,
        name: test.name,
        category: normalizeCategory(test.category),
        rationale: test.rationale || "Added manually",
        linkedDiagnoses: [],
        priority: test.priority || "routine",
        isCustom: true,
      };

      if (!prev) {
        return {
          examFindings: [],
          exam2Extras: [],
          tests: [newTest],
          testingStrategy: "",
          considerations: [],
        };
      }

      return {
        ...prev,
        tests: [...prev.tests, newTest],
      };
    });
  }, []);

  /** Add a custom examination item (Events Plan Exam section). */
  const addExamFinding = useCallback((finding) => {
    setPlanData((prev) => {
      const newFinding = {
        id: `custom-exam-${Date.now()}`,
        name: finding.name,
        rationale: finding.rationale || "Added manually",
        linkedDiagnoses: finding.linkedDiagnoses || [],
        anticipatedFindings: Array.isArray(finding.anticipatedFindings)
          ? finding.anticipatedFindings
          : [],
        isCustom: true,
      };

      if (!prev) {
        return {
          examFindings: [newFinding],
          exam2Extras: [],
          tests: [],
          testingStrategy: "",
          considerations: [],
        };
      }

      return {
        ...prev,
        examFindings: [...(prev.examFindings || []), newFinding],
      };
    });
  }, []);

  /**
   * Clear all plan data
   */
  const clearPlan = useCallback(() => {
    setPlanData(null);
    setError(null);
    lastRequestRef.current = "";
  }, []);

  /**
   * Retry generation
   */
  const retry = useCallback(
    (transcript, selectedDiagnoses, patientContext = null) => {
      lastRequestRef.current = "";
      setError(null);
      if (transcript && selectedDiagnoses) {
        generatePlan(transcript, selectedDiagnoses, patientContext);
      }
    },
    [generatePlan]
  );

  /**
   * Directly set planData (for hydrating from saved session)
   */
  const setPlanDataDirectly = useCallback((data) => {
    console.log('[useDiagnosticPlan] setPlanDataDirectly called with:', data);
    setPlanData(data);
  }, []);

  return {
    // State
    planData,
    loading,
    error,
    rawResponse,

    // Computed
    hasTests: (planData?.tests?.length || 0) > 0,
    hasExamFindings: (planData?.examFindings?.length || 0) > 0,

    // Actions
    generatePlan,
    addTest,
    addExamFinding,
    clearPlan,
    retry,
    setPlanData: setPlanDataDirectly, // For session hydration
  };
}

export { processExamFindings, processExam2Extras, processTests };
export default useDiagnosticPlan;
